/** Files list preview modal segment (distinct from AuthLayout `/file/:fileId`). */
export const PREVIEW_ROUTE_SEGMENT = 'preview';

const PREVIEW_IN_PATH_RE = /\/preview\/[^/]+/;

/** True when pathname includes `/preview/:fileId`. */
export function isPreviewRoutePath(pathname: string): boolean {
  return PREVIEW_IN_PATH_RE.test(pathname);
}

/** Last `/preview/:fileId` segment on a files list URL (layout-safe; no useParams). */
export function parsePreviewFileIdFromPath(pathname: string): string | undefined {
  if (!pathname.startsWith('/files/') || !isPreviewRoutePath(pathname)) return undefined;
  const match = pathname.match(/\/preview\/([^/]+)$/);
  return match?.[1];
}

/**
 * `location.state` set when the app pushed the preview on top of its own list entry,
 * so closing can `navigate(-1)` instead of stacking a duplicate list entry.
 */
export const PREVIEW_PUSHED_STATE = { previewPushed: true } as const;

export function isPreviewPushedState(state: unknown): boolean {
  return (state as { previewPushed?: unknown } | null)?.previewPushed === true;
}

/** Remove `/preview/:fileId` from a files list pathname. */
export function stripPreviewPath(pathname: string): string {
  const stripped = pathname.replace(PREVIEW_IN_PATH_RE, '');
  return stripped === '' ? '/' : stripped;
}

/**
 * Append preview segment to a list base path (`/files/personal`, `/files/personal/folder/9`).
 * Strips an existing preview suffix on `listPath` first.
 */
export function buildPreviewPath(listPath: string, fileId: number | string): string {
  const base = stripPreviewPath(listPath).replace(/\/$/, '') || '/';
  return `${base}/${PREVIEW_ROUTE_SEGMENT}/${fileId}`;
}

/** Swap preview file id when already on a preview route; otherwise append preview. */
export function replacePreviewFileIdInPath(pathname: string, fileId: number | string): string {
  if (isPreviewRoutePath(pathname)) {
    return pathname.replace(/\/preview\/[^/]+$/, `/${PREVIEW_ROUTE_SEGMENT}/${fileId}`);
  }
  return buildPreviewPath(pathname, fileId);
}
